import { useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import {
  Zap, Activity, Send, FileText, Shield, Users, Cpu,
  Layers, CandlestickChart, CalendarDays, ScrollText,
  Link as LinkIcon, CalendarCheck, CreditCard, Package,
  Wallet, Network, LayoutDashboard, Menu, X, User, LogOut,
  ArrowLeftRight, Briefcase, Star, Receipt, Palette,
} from 'lucide-react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import PriceTicker from './PriceTicker';
import ToastContainer from '../ui/ToastContainer';
import ErrorBoundary from '../feedback/ErrorBoundary';
import { useSessionStore } from '../../session/store';
import { MOBILE_NAV, USER_NAV, OPERATOR_NAV, ADMIN_NAV, OPERATOR_ADMIN_NAV, ROUTES } from '../../routes/constants';
import { cn } from '../../lib/utils';
import { useBrokerGate }        from '../../pages/broker/useBrokerGate';
import { BrokerTokenGateModal } from '../../pages/broker/BrokerTokenGateModal';
import DisclaimerModal          from './DisclaimerModal';

const MOB_ICON_MAP = {
  Zap, Activity, Send, FileText, Shield, Users, Cpu, Layers,
  CandlestickChart, CalendarDays, ScrollText, Link: LinkIcon,
  CalendarCheck, CreditCard, Package, Wallet, Network,
  LayoutDashboard, Briefcase, Star, Receipt, Palette,
};

// ─── More Drawer ───────────────────────────────────────────────────────────────
function MoreDrawer({ open, onClose }) {
  const { workspace, role, clearSession, user, canAdmin, setWorkspace } = useSessionStore();
  const isSuperAdmin = useSessionStore((s) => s.isSuperAdmin);
  const allNav = (workspace === 'admin'
    ? (role === 'operator' ? OPERATOR_ADMIN_NAV : ADMIN_NAV)
    : USER_NAV.filter((item) => !MOBILE_NAV.some((m) => m.to === item.to))
  ).filter((item) => !item.superAdminOnly || isSuperAdmin);
  const initial = (user?.name || user?.username || 'U')[0].toUpperCase();

  if (!open) return null;

  return (
    <div className="lg:hidden fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Bottom sheet */}
      <div className="absolute bottom-0 left-0 right-0 bg-bg-card border-t border-bg-border rounded-t-2xl max-h-[82vh] flex flex-col animate-slide-up">

        {/* Handle + header */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber/15 border border-amber/25 flex items-center justify-center">
              <span className="text-amber text-xs font-bold font-mono">{initial}</span>
            </div>
            <div className="leading-tight">
              <div className="text-xs font-medium text-txt-primary">{user?.name || user?.username || 'User'}</div>
              <div className="text-2xs text-txt-muted font-mono">{user?.email || ''}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-11 h-11 -m-2.5 flex items-center justify-center rounded-lg hover:bg-bg-hover text-txt-muted shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* Workspace switcher (admin only) */}
        {canAdmin && canAdmin() && (
          <div className="px-3 pb-2 shrink-0">
            <button
              onClick={() => { setWorkspace(workspace === 'admin' ? 'user' : 'admin'); onClose(); }}
              className={cn(
                'flex items-center gap-2 w-full px-3 py-2.5 rounded-lg text-xs font-mono border transition-colors',
                workspace === 'admin'
                  ? 'bg-amber/10 text-amber border-amber/25'
                  : 'bg-bg-hover/50 text-txt-secondary border-bg-border'
              )}
            >
              <ArrowLeftRight size={13} />
              <span>{workspace === 'admin' ? 'Switch to User Workspace' : `Switch to ${role === 'operator' ? 'Operator' : 'Admin'} Workspace`}</span>
            </button>
          </div>
        )}

        <div className="h-px bg-bg-border/60 shrink-0 mx-3" />

        {/* Nav items */}
        <div className="overflow-y-auto flex-1 px-3 py-2">
          <div className="grid grid-cols-2 gap-1">
            {allNav.map(({ to, label, icon }) => {
              const Icon = MOB_ICON_MAP[icon] || Zap;
              return (
                <NavLink
                  key={to}
                  to={to}
                  onClick={onClose}
                  className={({ isActive }) => cn(
                    'flex items-center gap-2.5 px-3 py-3 rounded-lg text-sm transition-colors',
                    isActive
                      ? 'bg-amber/10 text-amber font-medium'
                      : 'text-txt-secondary hover:bg-bg-hover hover:text-txt-primary'
                  )}
                >
                  <Icon size={16} className="shrink-0" />
                  <span className="text-xs font-medium truncate">{label}</span>
                </NavLink>
              );
            })}
          </div>
        </div>

        <div className="h-px bg-bg-border/60 shrink-0 mx-3" />

        {/* Footer: Profile + Logout */}
        <div className="px-3 py-2 shrink-0 flex items-center gap-2 pb-6">
          <Link
            to={ROUTES.PROFILE}
            onClick={onClose}
            className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-txt-secondary hover:bg-bg-hover transition-colors"
          >
            <User size={15} />
            <span className="text-xs font-medium">Profile</span>
          </Link>
          <button
            onClick={() => { clearSession(); onClose(); }}
            className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-txt-muted hover:bg-bear/10 hover:text-bear transition-colors"
          >
            <LogOut size={15} />
            <span className="text-xs font-medium">Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Mobile Bottom Nav ─────────────────────────────────────────────────────────
function MobileBottomNav() {
  const { workspace } = useSessionStore();
  const [showMore, setShowMore] = useState(false);

  const primaryItems = workspace === 'admin'
    ? [
        { to: '/admin/overview', label: 'Overview', icon: 'Shield' },
        { to: '/admin/users',    label: 'Users',    icon: 'Users' },
        { to: '/admin/runtime',  label: 'Runtime',  icon: 'Cpu' },
        { to: '/admin/dispatch', label: 'Dispatch', icon: 'Send' },
      ]
    : MOBILE_NAV;

  return (
    <>
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-bg-card border-t border-bg-border safe-area-pb">
        <div className="flex items-stretch">
          {primaryItems.map(({ to, label, icon }) => {
            const Icon = MOB_ICON_MAP[icon] || Zap;
            return (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) => cn(
                  'flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 text-2xs font-mono transition-colors',
                  isActive ? 'text-amber' : 'text-txt-muted hover:text-txt-secondary'
                )}
              >
                <Icon size={18} />
                <span>{label}</span>
              </NavLink>
            );
          })}

          {/* More button */}
          <button
            onClick={() => setShowMore(true)}
            className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 text-2xs font-mono text-txt-muted hover:text-txt-secondary transition-colors"
          >
            <Menu size={18} />
            <span>More</span>
          </button>
        </div>
      </nav>

      <MoreDrawer open={showMore} onClose={() => setShowMore(false)} />
    </>
  );
}

// ─── App Shell ─────────────────────────────────────────────────────────────────
export default function AppShell() {
  const { ready, token } = useSessionStore();
  const isAuthenticated = ready && !!token;
  const { showGate, gateReason, dismissGate, onGateSuccess } = useBrokerGate({ isAuthenticated });
  const location = useLocation();
  const isStrategyBuilderRoute = /^\/strategies\/(new|\d+\/edit)$/.test(location.pathname);

  return (
    <div className="flex h-dvh overflow-hidden bg-bg-base">
      {/* Sidebar — hidden on mobile */}
      <div className="hidden lg:flex">
        <Sidebar />
      </div>

      <div className="flex flex-col flex-1 min-w-0">
        <Header />
        <PriceTicker />
        <main className={cn('flex-1 overflow-y-auto p-3 lg:p-5 lg:pb-6', isStrategyBuilderRoute ? 'pb-3' : 'pb-20')}>
          <ErrorBoundary>
            <div className="animate-fade-in">
              <Outlet />
            </div>
          </ErrorBoundary>
        </main>
      </div>

      {!isStrategyBuilderRoute && <MobileBottomNav />}
      <ToastContainer />
      <BrokerTokenGateModal
        open={showGate}
        reason={gateReason}
        onSuccess={onGateSuccess}
        onDismiss={dismissGate}
      />
      <DisclaimerModal />
    </div>
  );
}
